import { create } from 'zustand';
import { useEffect } from 'react';
import { sessionService, type UserRole } from '@shared/services/session.service';
import { ALL_MERCHANT_SERVICES, type MerchantService } from '@shared/types/merchant';

const ENABLED_SERVICES_KEY = 'merchant-enabled-services';

interface User {
  email: string;
  name?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  error: string | null;
  role: UserRole;
  /** The ID of the currently authenticated merchant (only meaningful when role === 'merchant') */
  merchantId: string | null;
  /** Services enabled for the current merchant (only meaningful when role === 'merchant') */
  enabledServices: MerchantService[];
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  checkSession: () => void;
  clearError: () => void;
  setRole: (role: UserRole) => void;
  setEnabledServices: (services: MerchantService[]) => void;
}

type AuthStore = AuthState & AuthActions;

/**
 * Load the merchant's enabled services from localStorage.
 * When nothing has been explicitly configured yet, default to ALL services
 * so a freshly-logged-in merchant sees the full navigation instead of an
 * empty, over-filtered menu.
 */
function loadEnabledServices(): MerchantService[] {
  try {
    const raw = localStorage.getItem(ENABLED_SERVICES_KEY);
    if (!raw) return [...ALL_MERCHANT_SERVICES];
    const parsed = JSON.parse(raw) as MerchantService[];
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : [...ALL_MERCHANT_SERVICES];
  } catch {
    return [...ALL_MERCHANT_SERVICES];
  }
}

export const useAuthStore = create<AuthStore>((set) => ({
  // Initial state
  isAuthenticated: false,
  user: null,
  isLoading: true,
  error: null,
  role: sessionService.getRole(),
  merchantId: sessionService.getMerchantId(),
  enabledServices: loadEnabledServices(),

  // Actions
  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    
    try {
      sessionService.saveSession(email, password);
      
      const user: User = {
        email,
        name: email.split('@')[0],
      };
      
      set({
        isAuthenticated: true,
        user,
        isLoading: false,
        error: null,
        role: sessionService.getRole(),
        merchantId: sessionService.getMerchantId(),
        enabledServices: loadEnabledServices(),
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      set({ 
        isLoading: false, 
        error: errorMessage 
      });
      throw new Error(errorMessage);
    }
  },

  logout: () => {
    sessionService.logout();
    set({ 
      isAuthenticated: false, 
      user: null, 
      error: null 
    });
    window.location.href = '/login';
  },

  checkSession: () => {
    const session = sessionService.getSession();
    
    if (session && session.isAuthenticated) {
      const user: User = {
        email: session.email,
        name: session.email.split('@')[0],
      };
      set({
        isAuthenticated: true,
        user,
        isLoading: false,
        error: null,
        role: session.role ?? sessionService.getRole(),
        merchantId: sessionService.getMerchantId(),
        enabledServices: loadEnabledServices(),
      });
    } else {
      set({ 
        isAuthenticated: false, 
        user: null,
        isLoading: false,
      });
    }
  },

  clearError: () => {
    set({ error: null });
  },

  setRole: (role: UserRole) => {
    sessionService.setRole(role);
    set({ role, merchantId: sessionService.getMerchantId() });
  },

  setEnabledServices: (services: MerchantService[]) => {
    localStorage.setItem(ENABLED_SERVICES_KEY, JSON.stringify(services));
    set({ enabledServices: services });
  },
}));

/**
 * Hook that wires up automatic logout when the API signals a 401 Unauthorized.
 * Attach this once at the top of the component tree (e.g. App.tsx or AuthProvider).
 */
export function useUnauthorizedHandler() {
  const logout = useAuthStore((s) => s.logout);

  useEffect(() => {
    const handleUnauthorized = () => logout();
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [logout]);
}
